import { isFunction, isArray, isString, trim, isEmpty, isNil, eq, includes, flatten } from 'lodash-es';
import 'reflect-metadata';
import * as i from 'inflected';
import S from 'string';

// export type Constructor<T>
// this won't work till I target es2015, @see http://stackoverflow.com/a/41102182/1248812
// export class CustomError extends Error {
//     constructor(message) {
//         super();
//         this.message = message;
//         this.stack = (new Error()).stack;
//         this.name = this.constructor.name;
//     }
// }
//
// export class IllegalStateError extends CustomError {
//     constructor(message) {
//         super(message);
//     }
// }
const lang = {
    abstractMethod: function (constructor, name) {
        throw new Error(`AbstractMethod: ${constructor.name} must implement abstract method ${name}`);
    },
    illegalState: function (message) {
        throw new Error(`IllegalState: ${message}`);
    },
    illegalArgument: function (message) {
        throw new Error('IllegalArgument: ' + message);
    },
    notImplemented: function (message) {
        throw new Error('MethodNotImplemented: ' + message);
    },
    methodNotSupported: function (reason) {
        throw new Error('MethodNotSupported' + reason ? `: ${reason}` : '');
    },
    'try': function (func) {
        return isFunction(func) ? func() : null;
    },
    wrapInArray: function (arrayOrOtherwise) {
        return isArray(arrayOrOtherwise) ? arrayOrOtherwise : [arrayOrOtherwise];
    },
    constructorSuper(constructor) {
        return Object.getPrototypeOf(constructor.prototype).constructor;
    },
    isBlank(s) {
        // empty strings are falsey
        return isString(s) ? !trim(s) : isEmpty(s);
    }
};

const assert = {
    // todo: have this delegate to notNil then remove.
    notNull: (value, name = 'value') => {
        if (!isString(value) && !value)
            throw new Error(`expected ${name} to be non null, got: ${typeof value}`);
        return value;
    },
    notNil: (value, name = 'value') => {
        if (isNil(value))
            throw new Error(`expected ${name} to be non null, got: ${typeof value}`);
        return value;
    },
    isNil: (value, name = 'value') => {
        if (!isNil(value))
            throw new Error(`expected ${name} to be null or undefined, got: ${typeof value}`);
        return value;
    },
    notPresent: function (value, name = 'value') {
        if (value != null && typeof value !== 'undefined')
            throw new Error(`expected ${name} to be null or undefined, got: ${value}`);
    },
    notEmpty: function (value, name = 'value') {
        assert.notNull(value, name);
        assert.isInteger(value.length);
        if (value.length < 1)
            throw new Error(`expected ${name} have length > 0, got: ${value.length}`);
        return value;
    },
    notBlank: function (value, name = 'value') {
        assert.isString(value);
        assert.isInteger(value.length);
        if (value.length < 1)
            throw new Error(`expected ${name} have length > 0, got: ${value.length}`);
        return value;
    },
    isArray(array, name = 'value') {
        if (!isArray(array)) {
            throw new Error(`expected ${name} to be an array, got: ${typeof array}`);
        }
        return array;
    },
    isFunction(thing, message) {
        if (!(isFunction(thing))) {
            throw new Error(message || `expected to get a function, got: ${typeof thing}`);
        }
        return thing;
    },
    isTruthy: (value, message) => {
        if (!value) {
            throw new Error(message || `expected to get a truthy value, got: ${value}`);
        }
        return value;
    },
    isFalsey: (value, message) => {
        if (value) {
            throw new Error(message || `expected to get a falsey value, got: ${value}`);
        }
        return value;
    },
    isInteger: (value) => {
        if (!((typeof value === 'number') && Math.floor(value) === value)) {
            throw new Error(`expected to get an integer, got a ${typeof value} with value ${value}`);
        }
        return value;
    },
    isNumber: (value) => {
        if (!(typeof value === 'number')) {
            throw new Error(`expected to get a number, got a ${typeof value} with value ${value}`);
        }
        return value;
    },
    isString(value) {
        if (!(typeof value === 'string')) {
            throw new Error(`expected to get a string, got a ${typeof value} with value ${value}`);
        }
        return value;
    },
    defined: (target, propertyName) => {
        if (typeof target[propertyName] === 'undefined')
            throw new Error(`property ${propertyName} is undefined`);
    },
    equal: (a, b) => {
        if (!eq(a, b)) {
            throw new Error(`expected items to be equal, got ${a} and ${b}`);
        }
    },
    value(value) {
        return {
            isOneOf(items) {
                if (!includes(items, value)) {
                    throw new Error(`expected to get one of ${items.join(',')}, got a ${typeof value} with value ${value}`);
                }
                return value;
            },
            isInstanceOrNull: (type, message) => {
                if (value && !(value instanceof type)) {
                    throw new Error(message || `expected to get null or instance of ${type.name}, got: ${typeof value}`);
                }
                return value;
            },
            isInstance: (type, message) => {
                if (!(value instanceof type)) {
                    throw new Error(message || `expected to get null or instance of ${type.name}, got: ${typeof value}`);
                }
                return value;
            }
        };
    },
    list(items) {
        return {
            onlyContains(constraints) {
                items.forEach(item => {
                    if (!includes(constraints, item)) {
                        throw new Error(`expected to get one of ${constraints.join(',')}, got a ${typeof items} with value ${items}`);
                    }
                });
                return items;
            }
        };
    },
    oneOrNoneOf(options, a, b, callback, otherwise) {
        if (options) {
            if (!isNil(options[a]) && !isNil(options[b])) {
                lang.illegalState(`expected either one of none of '${a.toString()}' and '${b.toString()}' but got both`);
            }
            if (!isNil(options[a]) || !isNil(options[b])) {
                return callback();
            }
            else {
                return otherwise;
            }
        }
        else {
            return otherwise;
        }
    },
    oneOf(options, a, b) {
        if (!isNil(options[a]) && !isNil(options[b])) {
            lang.illegalState(`expected only one of '${a.toString()}' and '${b.toString()}' but got both`);
        }
        if (isNil(options[a]) && isNil(options[b])) {
            lang.illegalState(`expected one of '${a.toString()}' and '${b.toString()}' but got none`);
        }
    }
};

class Either {
    constructor(value, error) {
        this.value = value;
        this.error = error;
    }
    isError() {
        return !isNil(this.error);
    }
    map(transform) {
        return this.isError() ?
            new Either(null, this.error) :
            new Either(transform(this.value), null);
    }
    mapError(transform) {
        return this.isError() ?
            new Either(null, transform(this.error)) :
            new Either(this.value, null);
    }
    andThen(transform) {
        if (this.isError()) {
            return new Either(null, this.error);
        }
        else {
            const value = transform(this.value);
            return new Either(value.value, value.error);
        }
    }
    flatMap(transform) {
        return this.andThen(transform);
    }
    tap(callback) {
        if (!this.isError()) {
            callback(this.value);
        }
        return this;
    }
    tapError(callback) {
        if (this.isError()) {
            callback(this.error);
        }
        return this;
    }
}
(function (Either) {
    function withValue(value) {
        return new Either(value, null);
    }
    Either.withValue = withValue;
    function withError(e) {
        return new Either(null, e);
    }
    Either.withError = withError;
    // /* tslint:disable:max-line-length */
    // export async function asyncPipe<A, AE, AM, B, BE, BM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operator: AsyncPipeOperator<A, AE, AM, B, BE, BM>
    // ): Promise<Result<B, BE, BM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>
    // ): Promise<Result<C, CE, CM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM, D, DE, DM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>,
    //   operatorC: AsyncPipeOperator<C, CE, CM, D, DE, DM>
    // ): Promise<Result<D, DE, DM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM, D, DE, DM, E, EE, EM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>,
    //   operatorC: AsyncPipeOperator<C, CE, CM, D, DE, DM>,
    //   operatorD: AsyncPipeOperator<D, DE, DM, E, EE, EM>
    // ): Promise<Result<E, EE, EM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM, D, DE, DM, E, EE, EM, F, FE, FM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>,
    //   operatorC: AsyncPipeOperator<C, CE, CM, D, DE, DM>,
    //   operatorD: AsyncPipeOperator<D, DE, DM, E, EE, EM>,
    //   operatorE: AsyncPipeOperator<E, EE, EM, F, FE, FM>
    // ): Promise<Result<F, FE, FM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM, D, DE, DM, E, EE, EM, F, FE, FM, G, GE, GM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>,
    //   operatorC: AsyncPipeOperator<C, CE, CM, D, DE, DM>,
    //   operatorD: AsyncPipeOperator<D, DE, DM, E, EE, EM>,
    //   operatorE: AsyncPipeOperator<E, EE, EM, F, FE, FM>,
    //   operatorG: AsyncPipeOperator<F, FE, FM, G, GE, GM>
    // ): Promise<Result<G, GE, GM>>
    //
    // export async function asyncPipe<A, AE, AM, B, BE, BM, C, CE, CM, D, DE, DM, E, EE, EM, F, FE, FM, G, GE, GM, H, HE, HM>(
    //   initialResult: Result<A, AE, AM> | Promise<Result<A, AE, AM>>,
    //   operatorA: AsyncPipeOperator<A, AE, AM, B, BE, BM>,
    //   operatorB: AsyncPipeOperator<B, BE, BM, C, CE, CM>,
    //   operatorC: AsyncPipeOperator<C, CE, CM, D, DE, DM>,
    //   operatorD: AsyncPipeOperator<D, DE, DM, E, EE, EM>,
    //   operatorE: AsyncPipeOperator<E, EE, EM, F, FE, FM>,
    //   operatorG: AsyncPipeOperator<F, FE, FM, G, GE, GM>,
    //   operatorH: AsyncPipeOperator<G, GE, GM, H, HE, HM>
    // ): Promise<Result<H, HE, HM>>
    //
    // /* tslint:enable:max-line-length */
    //
    // export async function asyncPipe(initialResult: Result<any, any, any> | Promise<Result<any, any, any>>, ...operations: AsyncPipeOperator<any, any, any, any, any, any>[]): Promise<Result<any, any, any>> {
    //   if (operations.length === 0) {
    //     return Promise.resolve(initialResult);
    //   }
    //   let result = Promise.resolve(initialResult);
    //   for (let i = 0; i < operations.length; i++) {
    //     result = Promise.resolve(operations[i](await result));
    //   }
    //   return result;
    // }
})(Either || (Either = {}));

function getOwnMetadataList(klass, name) {
    let ownMetadata = Reflect.getOwnMetadata(name, klass);
    if (isNil(ownMetadata)) {
        ownMetadata = [];
        Reflect.defineMetadata(name, ownMetadata, klass);
    }
    assert.equal(ownMetadata, Reflect.getOwnMetadata(name, klass));
    return ownMetadata;
}
class Klass {
    constructor(klass) {
        this.klass = klass;
    }
    /**
     * Returns the prototype chain of the object (including itself) with the first element
     * being the object itself.
     * @param options
     * @returns {any[]}
     */
    prototypeChain(options = { upto: Object }) {
        let prototypes = [this.klass.prototype];
        let proto = this.klass.prototype;
        while (true) {
            proto = Object.getPrototypeOf(proto);
            if (!proto) {
                break;
            }
            prototypes.push(proto);
            if (proto.constructor == options.upto) {
                break;
            }
        }
        return prototypes;
    }
    /**
     * Returns the constructor can of the class (including itself) with the first element
     * being the class itself.
     * @param options
     * @returns {any[]}
     */
    constructorChain(options) {
        let constructors = [];
        let currentConstructor = this.klass;
        const uptoConstructor = options ? options.upto : null;
        while (true) {
            if (!currentConstructor) {
                break;
            }
            constructors.push(currentConstructor);
            if (currentConstructor == uptoConstructor || currentConstructor == Object) {
                break;
            }
            currentConstructor = Klass.constructorSuper(currentConstructor);
        }
        return constructors;
    }
    defineMetadata(name, value) {
        assert.isFalsey(this.hasOwnMetadata(name), `metadata is already defined for ${name.toString()}`);
        Reflect.defineMetadata(name, value, this.klass);
        return value;
    }
    hasOwnMetadata(name) {
        return Reflect.hasOwnMetadata(name, this.klass);
    }
    getOwnMetadataList(name) {
        return [...getOwnMetadataList(this.klass, name)];
    }
    addToOwnMetadataList(name, value) {
        getOwnMetadataList(this.klass, name).push(value);
    }
    getOwnMetadata(name) {
        return Reflect.getOwnMetadata(name, this.klass);
    }
    getMetadata(name) {
        return Reflect.getMetadata(name, this.klass);
    }
    /**
     * Returns a flattened list of all meta data items defined in the
     * class hierarchy.  The items start with those on the target
     * class and parents added after that.
     * @param name
     * @param options
     */
    getMetadataList(name, options) {
        return flatten(this.constructorChain(options)
            .map(ctor => getOwnMetadataList(ctor, name))
            .filter(v => !isNil(v)));
    }
}
(function (Klass) {
    function get(klass) {
        const ctor = isFunction(klass) ? klass : klass.constructor;
        return new Klass(ctor);
    }
    Klass.get = get;
    function constructorSuper(constructor) {
        return Object.getPrototypeOf(constructor.prototype).constructor;
    }
    Klass.constructorSuper = constructorSuper;
})(Klass || (Klass = {}));

class Mixin {
}
(function (Mixin) {
    function createProxy(original, mixin) {
        return new Proxy(original, new Handler(original, mixin));
    }
    Mixin.createProxy = createProxy;
    class Handler {
        // @ts-ignore
        constructor(original, mixin) {
            this.original = original;
            this.mixin = mixin;
        }
        // @ts-ignore
        get(target, property, receiver) {
            // @ts-ignore TODO: Should this be Object.hasOwnProperty? what if it's a chain of proxies??
            return !isNil(this.mixin[property]) ? this.mixin[property] : target[property];
        }
    }
    Mixin.Handler = Handler;
})(Mixin || (Mixin = {}));

class Optional {
    constructor(value) {
        this.value = value;
    }
    static of(value) {
        return !isNil(value) ? new Optional.Present(value) : new Optional.Nothing();
    }
    static nothing() {
        return new Optional.Nothing();
    }
    isEmpty() {
        return isNil(this.value);
    }
    map(func) {
        return this.isEmpty() ? Optional.nothing() : Optional.of(func(this.value));
    }
    otherwiseRaise(errorFactory) {
        return this.isEmpty() ? Either.withError(errorFactory()) : Either.withValue(this.value);
    }
    otherwise(value) {
        return this.isEmpty() ? Either.withValue(value) : Either.withValue(this.value);
    }
    getOr(defaultValue) {
        if (isNil(defaultValue)) {
            lang.illegalState('defaultValue can\'t be nil');
        }
        return this.isEmpty() ? defaultValue : this.value;
    }
}
(function (Optional) {
    class Present extends Optional {
        constructor(result) {
            super(result);
        }
    }
    Optional.Present = Present;
    class Nothing extends Optional {
        constructor() {
            super(null);
        }
    }
    Optional.Nothing = Nothing;
})(Optional || (Optional = {}));

class Opts {
    constructor(options) {
        this.options = options;
    }
    static get(options) {
        return new Opts(options);
    }
    getOptional(key, defaultValue) {
        if (isNil(this.options) || isNil(this.options[key])) {
            return isFunction(defaultValue) ? defaultValue() : defaultValue;
        }
        else {
            return this.options[key];
        }
    }
}

const ALL_CAPS = /^[A-Z_-]+$/;
class Stringy {
    constructor(value) {
        this.value = value;
    }
    static from(value) {
        const base = ALL_CAPS.test(value) ? value.toLowerCase() : value;
        return new Stringy(base);
    }
    pluralize() {
        return new Stringy(i.pluralize(this.value));
    }
    camelize(upperCaseFirstLetter = true) {
        // S will camemlise dasherised strings to lowerCaseForm where as the inflector won't.
        // so we camelise first then apply the case change using the inflector
        const term = S(this.value).camelize().toString();
        return new Stringy(i.camelize(term, upperCaseFirstLetter));
    }
    camelizeLower() {
        return this.camelize(false);
    }
    capitalise() {
        return new Stringy(S(this.dasherize().toString()).capitalize().toString());
    }
    dasherize() {
        return new Stringy(S(this.value).dasherize().toString().replace(/^-/, ''));
    }
    underscore() {
        return new Stringy(S(this.value).underscore().toString().replace(/^_/, ''));
    }
    singularize() {
        return new Stringy(i.singularize(this.value));
    }
    equals(other) {
        return other.toString() === this.value;
    }
    toString() {
        return this.value;
    }
    valueOf() {
        return this.value;
    }
}

/**
 * TODO: this is an either with meta that was useful when building a restilence
 *       server in typescript as db queries would return
 */
class Result {
    constructor(value, error, meta) {
        this.value = value;
        this.error = error;
        this.meta = meta;
    }
    isError() {
        return !isNil(this.error);
    }
    map(transform) {
        return this.isError() ?
            new Result(undefined, this.error, this.meta) :
            new Result(transform(this.value), undefined, this.meta);
    }
    mapError(transform) {
        return this.isError() ?
            new Result(undefined, transform(this.error), this.meta) :
            new Result(this.value, undefined, this.meta);
    }
    andThen(transform) {
        if (this.isError()) {
            return new Result(undefined, this.error, undefined);
        }
        else {
            const value = transform(this.value, this.meta);
            return new Result(value.value, value.error, value.meta);
        }
    }
    flatMap(transform) {
        return this.andThen(transform);
    }
    tap(callback) {
        if (!this.isError()) {
            callback(this.value, this.meta);
        }
        return this;
    }
    tapError(callback) {
        if (this.isError()) {
            callback(this.error);
        }
        return this;
    }
    withMeta(meta) {
        return new Result(this.value, this.error, meta);
    }
}
(function (Result) {
    function withValue(value) {
        return new Result(value, undefined, undefined);
    }
    Result.withValue = withValue;
    function withValueAndMeta(value, meta) {
        return new Result(value, undefined, meta);
    }
    Result.withValueAndMeta = withValueAndMeta;
    function withMeta(meta) {
        return new Result(undefined, undefined, meta);
    }
    Result.withMeta = withMeta;
    function withError(e) {
        return new Result(undefined, e, undefined);
    }
    Result.withError = withError;
    /* tslint:enable:max-line-length */
    async function asyncPipe(initialResult, ...operations) {
        if (operations.length === 0) {
            return Promise.resolve(initialResult);
        }
        let result = Promise.resolve(initialResult);
        for (let i = 0; i < operations.length; i++) {
            result = Promise.resolve(operations[i](await result));
        }
        return result;
    }
    Result.asyncPipe = asyncPipe;
})(Result || (Result = {}));

var AsyncPipeOperator;
(function (AsyncPipeOperator) {
    function map(mapFunction) {
        return async (result) => {
            return result.isError() ?
                new Result(undefined, result.error, result.meta) :
                new Result(await mapFunction(result.value), undefined, result.meta);
        };
    }
    AsyncPipeOperator.map = map;
    function mapError(mapFunction) {
        return async (result) => {
            return result.isError() ?
                new Result(undefined, await mapFunction(result.error), result.meta) :
                new Result(result.value, undefined, result.meta);
        };
    }
    AsyncPipeOperator.mapError = mapError;
    function andThen(mapFunction) {
        return async (previousResult) => {
            if (previousResult.isError()) {
                return new Result(undefined, previousResult.error, undefined);
            }
            else {
                const value = await mapFunction(previousResult.value, previousResult.meta);
                return new Result(value.value, value.error, value.meta);
            }
        };
    }
    AsyncPipeOperator.andThen = andThen;
    function tap(tapFunction) {
        return async (result) => {
            if (!result.isError()) {
                await tapFunction(result.value, result.meta);
            }
            return result;
        };
    }
    AsyncPipeOperator.tap = tap;
    function tapError(tapFunction) {
        return async (result) => {
            if (result.isError()) {
                await tapFunction(result.error);
            }
            return result;
        };
    }
    AsyncPipeOperator.tapError = tapError;
    function andFinally(tapFunction) {
        return async (result) => {
            await tapFunction(result);
            return result;
        };
    }
    AsyncPipeOperator.andFinally = andFinally;
})(AsyncPipeOperator || (AsyncPipeOperator = {}));

class SelfAndDescendantsVisitor {
    constructor() {
    }
    visit(it) {
        this.onVisit(it);
        if (this.shouldVisitChildrenOf(it)) {
            it.visitChildren(this);
        }
    }
    shouldVisitChildrenOf(_) {
        return true;
    }
}
(function (SelfAndDescendantsVisitor) {
    class Each extends SelfAndDescendantsVisitor {
        constructor(callback) {
            super();
            this.callback = callback;
        }
        onVisit(it) {
            this.callback(it);
        }
    }
    SelfAndDescendantsVisitor.Each = Each;
})(SelfAndDescendantsVisitor || (SelfAndDescendantsVisitor = {}));

/**
 * @file Automatically generated by barrelsby.
 */

// Tricking semver to bump the version again, and again..

/**
 * Generated bundle index. Do not edit.
 */

export { AsyncPipeOperator, Either, Klass, Mixin, Optional, Opts, Result, SelfAndDescendantsVisitor, Stringy, assert, lang };
